// WhatsNewTour — a swipeable 5-slide intro that lives on the Home
// screen and surfaces every shiny new thing we just shipped:
//   1. Talk-to-text everywhere
//   2. YouTube-style channels + donations
//   3. Floating mini-player with PiP
//   4. Face-recognition sign in
//   5. Weekly rhythm + study journal
//
// The dismiss state is persisted in localStorage under a versioned key
// so we can ship another tour later just by bumping the version.
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  MicrophoneStage, BellSimpleRinging, PictureInPicture, ScanSmiley,
  Calendar, ArrowLeft, ArrowRight, X, Sparkle, HandsPraying, MonitorPlay,
  Palette, FilmStrip, Crown,
} from "@phosphor-icons/react";

// Bump TOUR_VERSION whenever you want every user to see the next batch
// of "What's New" slides exactly once. The dismiss flag is keyed off
// this string in localStorage.
const TOUR_VERSION = "v2-2026-02-channels-themes-cast";
const TOUR_KEY = `holy_words_whats_new_${TOUR_VERSION}`;

const SLIDES = [
  {
    icon: Crown,
    badge: "Premium",
    title: "Start your own channel",
    body: "Premium supporters now get a YouTube-style channel — banner, vanity URL (holywords144.com/c/your-name), donation handles, and a real subscriber count. Try 3 months for just $12.",
    cta: { label: "See pricing", to: "/support" },
    accent: "from-amber-500 to-amber-600",
  },
  {
    icon: HandsPraying,
    badge: "Prayer beacons",
    title: "Friends asking for prayer",
    body: "When a friend taps 'pray with me', a praying-hands badge lights up in your header. Tap it to see who's asking and start a video prayer call right from the sheet.",
    cta: { label: "Open prayer wall", to: "/prayer-wall" },
    accent: "from-terracotta to-terracotta-600",
  },
  {
    icon: MonitorPlay,
    badge: "Cast & AirPlay",
    title: "Push it to your TV",
    body: "Every player now has a Cast button on Chrome/Android and a native AirPlay control on Safari/iOS. Push gospel music, sermons, and films straight to your living-room TV.",
    cta: { label: "Browse sermons", to: "/sermons" },
    accent: "from-forest to-forest-600",
  },
  {
    icon: FilmStrip,
    badge: "337 films",
    title: "Black American film library",
    body: "Foundational, religious, Spike Lee joints, Civil Rights documentaries — all in one library with sub-genre filters, plus a 'Watch Lineup' queue you can save.",
    cta: { label: "Browse the library", to: "/movies/black-american" },
    accent: "from-cream-500 to-cream-600",
  },
  {
    icon: Palette,
    badge: "Customize",
    title: "Pick your theme",
    body: "Tap the paint-palette icon in the header — 6 themes including Midnight Worship, Royal Gospel, and Forest Cathedral. Your choice saves to this device.",
    cta: { label: "Open home", to: "/" },
    accent: "from-purple-500 to-indigo-600",
  },
];

export default function WhatsNewTour() {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const startX = useRef(null);
  const location = useLocation();

  useEffect(() => {
    try {
      if (!localStorage.getItem(TOUR_KEY)) setOpen(true);
    } catch (_) {}
  }, []);

  // If the user is anywhere OTHER than the home / landing page,
  // suppress the tour entirely — they've already discovered the
  // app on their own and the dimmed overlay was sometimes blocking
  // taps on deeper pages like /profile (the "Quick Actions tiles
  // don't respond" bug the user reported).
  const onHome = location.pathname === "/" || location.pathname === "/home";
  useEffect(() => {
    if (open && !onHome) {
      // Snooze (not dismiss) — they'll see it next time they visit
      // home if they haven't dismissed it yet.
      setOpen(false);
    }
  }, [open, onHome]);

  const dismiss = () => {
    try { localStorage.setItem(TOUR_KEY, "seen"); } catch (_) {}
    setOpen(false);
  };
  const next = () => (i < SLIDES.length - 1 ? setI(i + 1) : dismiss());
  const prev = () => i > 0 && setI(i - 1);

  // touch swipe
  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx < -40) next();
    else if (dx > 40) prev();
    startX.current = null;
  };

  if (!open) return null;
  const s = SLIDES[i];
  const Icon = s.icon;

  return (
    <div className="fixed inset-0 z-[150] bg-ink/70 grid place-items-center p-4 animate-fade-up" data-testid="whats-new-tour" onClick={dismiss}>
      <div
        className="bg-cream rounded-[28px] w-full max-w-md overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        role="dialog"
        aria-modal="true"
        aria-labelledby="whats-new-title"
      >
        <div className={`relative aspect-[5/3] bg-gradient-to-br ${s.accent} p-6 flex items-end`}>
          <button onClick={dismiss} data-testid="whats-new-close" aria-label="Close" className="absolute top-3 right-3 w-9 h-9 grid place-items-center rounded-full bg-white/15 hover:bg-white/25 text-cream">
            <X size={14} weight="bold" />
          </button>
          <div className="text-cream">
            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase font-bold bg-white/15 px-3 py-1 rounded-full">
              <Sparkle size={11} weight="fill" /> {s.badge}
            </span>
            <Icon size={56} weight="duotone" className="mt-4 -mb-2 opacity-95" />
          </div>
        </div>

        <div className="p-6 md:p-7">
          <h2 id="whats-new-title" className="font-heading text-3xl text-forest leading-snug" data-testid={`whats-new-title-${i}`}>{s.title}</h2>
          <p className="text-ink/80 mt-3 leading-relaxed">{s.body}</p>

          <div className="mt-5 flex items-center justify-between gap-3">
            <div className="flex gap-1.5" data-testid="whats-new-dots">
              {SLIDES.map((_, k) => (
                <button
                  key={k}
                  onClick={() => setI(k)}
                  aria-label={`Go to slide ${k + 1}`}
                  data-testid={`whats-new-dot-${k}`}
                  className={`h-1.5 rounded-full transition-all ${k === i ? "w-6 bg-forest" : "w-1.5 bg-cream-300"}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={prev} disabled={i === 0} data-testid="whats-new-prev" aria-label="Previous" className="w-10 h-10 grid place-items-center text-ink/70 disabled:opacity-30 hover:bg-cream-300">
                <ArrowLeft size={14} weight="bold" />
              </button>
              <button onClick={next} data-testid="whats-new-next" className="px-5 h-10 text-forest hover:text-terracotta text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-forest-600">
                {i === SLIDES.length - 1 ? "Get going" : "Next"}
                <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-cream-300 pt-4">
            <button onClick={dismiss} className="text-xs text-muted_ink hover:text-forest" data-testid="whats-new-skip">
              Skip the tour
            </button>
            <Link to={s.cta.to} onClick={dismiss} data-testid={`whats-new-cta-${i}`} className="text-xs font-semibold text-terracotta hover:underline inline-flex items-center gap-1">
              {s.cta.label} <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
